import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useTimeFilter } from '../contexts/TimeFilterContext';
import PlannerLeafletMap from '../components/maps/PlannerLeafletMap';
import { fetchOsrmRoute } from '../components/maps/osrmRoute';
import { generateDriverZones, generateDriverColors } from '../components/maps/mapZones';
import {
  useMapsAssignment,
  useMultiloadRoutes,
  useAssignmentChunk,
} from '../queries/useAssignmentChunks';
import { useAssignmentRouteEnabled } from '../hooks/useAssignmentRouteEnabled';
import {
  attachDriverTrips,
  ASSIGNMENT_CHUNK_TYPES,
} from '../services/firebaseService';
import { RiRestTimeFill } from 'react-icons/ri';
import { Truck, Map, ArrowLeft, Ban, Car, MapPin, Clock, ArrowDown, Gauge, Navigation } from 'lucide-react';
import { sortTripsForDisplay, buildTripSections } from '../utils/tripSections';
import { sumDeadheadMilesForTotal } from '../utils/deadheadEta';
import { DriverFreezeTypeBadge, DriverMultiloadBadge } from '../components/DriverFreezeTypeBadge';
import { isFleetDriver } from '../utils/fleetWindows';

const MapsPage = () => {
  const { selectedResultId } = useTimeFilter();
  const mapsRouteEnabled = useAssignmentRouteEnabled('maps');
  const mapsQuery = useMapsAssignment(selectedResultId, { enabled: mapsRouteEnabled });
  const assignmentDataBase = mapsQuery.normalized;
  const [selectedDriver, setSelectedDriver] = useState(null);
  const [selectedTripId, setSelectedTripId] = useState(null);
  const [visibleDriverZones, setVisibleDriverZones] = useState([]);
  const [viewMode, setViewMode] = useState('route'); // 'route' or 'zone'

  const fullDriversQuery = useAssignmentChunk(selectedResultId, ASSIGNMENT_CHUNK_TYPES.DRIVERS, {
    enabled: Boolean(selectedDriver?.driver_id) || viewMode === 'zone',
  });
  const multiloadQuery = useMultiloadRoutes(selectedResultId, {
    enabled: mapsRouteEnabled && Boolean(selectedDriver?.driver_id),
  });

  const assignmentData = useMemo(() => {
    if (!assignmentDataBase) return null;
    return {
      ...assignmentDataBase,
      multiload_routes: multiloadQuery.chunkMap?.multiload_routes || assignmentDataBase.multiload_routes || {},
    };
  }, [assignmentDataBase, multiloadQuery.data]);

  const loading = mapsQuery.isLoading || (mapsQuery.isFetching && !assignmentDataBase);
  const loadingDriverTrips =
    Boolean(selectedDriver) &&
    fullDriversQuery.isLoading &&
    !(selectedDriver.trips && selectedDriver.trips.length > 0);

  // Debug selectedDriver changes
  React.useEffect(() => {
    console.log(`🔄 selectedDriver changed:`, selectedDriver);
    if (selectedDriver) {
      console.log(`   - Name: ${selectedDriver.driver_name}`);
      console.log(`   - ID: ${selectedDriver.driver_id} (type: ${typeof selectedDriver.driver_id})`);
    } else {
      console.log(`   - Set to null`);
    }
  }, [selectedDriver]);

  const [loadingRoutes, setLoadingRoutes] = useState(false);
  const [routes, setRoutes] = useState([]);

  const companyTimezone = useMemo(() => {
    return assignmentData?.metadata?.timezone || 'America/Los_Angeles';
  }, [assignmentData]);

  useEffect(() => {
    if (!selectedResultId) {
      setSelectedDriver(null);
      setSelectedTripId(null);
      setRoutes([]);
      return;
    }
    if (assignmentDataBase) {
      setSelectedDriver(null);
      setSelectedTripId(null);
      setRoutes([]);
    }
  }, [selectedResultId, assignmentDataBase?.id]);

  useEffect(() => {
    if (!selectedDriver?.driver_id || !fullDriversQuery.data) return;
    const withTrips = attachDriverTrips(
      assignmentDataBase?.drivers,
      fullDriversQuery.data,
      selectedDriver.driver_id
    );
    if (withTrips) {
      setSelectedDriver(withTrips);
    }
  }, [fullDriversQuery.data, selectedDriver?.driver_id, assignmentDataBase?.drivers]);

  const displayDrivers = useMemo(() => {
    const base = assignmentDataBase?.drivers || [];
    const hydrateFromFull = (lite) => {
      const full = fullDriversQuery.data?.find(
        (d) => String(d.driver_id) === String(lite.driver_id)
      );
      if (!full?.trips?.length) return lite;
      return { ...lite, trips: full.trips };
    };
    if (viewMode === 'zone' && fullDriversQuery.data) {
      return base.map(hydrateFromFull);
    }
    if (selectedDriver?.driver_id) {
      return base.map((lite) => {
        if (String(lite.driver_id) !== String(selectedDriver.driver_id)) return lite;
        if (selectedDriver.trips?.length) return { ...lite, trips: selectedDriver.trips };
        return hydrateFromFull(lite);
      });
    }
    return base;
  }, [assignmentDataBase?.drivers, viewMode, fullDriversQuery.data, selectedDriver?.driver_id, selectedDriver?.trips]);

  // Fetch complete route when driver is selected
  // Builds a continuous route: Driver → Pickup1 → Dropoff1 → Pickup2 → Dropoff2 → ...
  const fetchRoutesForDriver = useCallback(async (driver) => {
    if (!driver || !driver.trips || driver.trips.length === 0) {
      setRoutes([]);
      return;
    }

    setLoadingRoutes(true);

    // Sort trips by sequence
    const sortedTrips = [...driver.trips].sort((a, b) => (a.sequence || 0) - (b.sequence || 0));

    // Build list of all waypoints in order
    const waypoints = [];

    // Start with driver's location
    const driverLat = driver.current_latitude || driver.ref_latitude;
    const driverLng = driver.current_longitude || driver.ref_longitude;

    if (driverLat && driverLng) {
      waypoints.push({
        lat: driverLat,
        lng: driverLng,
        type: 'driver',
        label: 'Driver Start'
      });
    }

    // Count busy trips for labeling
    let busyCount = 0;
    sortedTrips.forEach(t => {
      if (t.is_busy_trip || t.sequence === 0) busyCount++;
    });

    let currentBusy = 0;
    let currentPlanned = 0;

    // Add all pickups and dropoffs in order
    for (const trip of sortedTrips) {
      const pickupLat = trip.pickup_lat || trip.origin?.latitude;
      const pickupLng = trip.pickup_lng || trip.origin?.longitude;
      const dropoffLat = trip.dropoff_lat || trip.destination?.latitude;
      const dropoffLng = trip.dropoff_lng || trip.destination?.longitude;

      const isBusy = trip.is_busy_trip || trip.sequence === 0;
      let displayNum;
      if (isBusy) {
        currentBusy++;
        displayNum = busyCount > 1 ? `B${currentBusy}` : 'B';
      } else {
        currentPlanned++;
        displayNum = currentPlanned;
      }

      if (pickupLat && pickupLng) {
        waypoints.push({
          lat: pickupLat,
          lng: pickupLng,
          type: 'pickup',
          tripNumber: displayNum,
          label: `Pickup ${displayNum}`,
          isBusy
        });
      }

      if (dropoffLat && dropoffLng) {
        waypoints.push({
          lat: dropoffLat,
          lng: dropoffLng,
          type: 'dropoff',
          tripNumber: displayNum,
          label: `Dropoff ${displayNum}`,
          isBusy
        });
      }
    }

    // Fetch route segments between consecutive waypoints
    const routeSegments = [];

    for (let i = 0; i < waypoints.length - 1; i++) {
      const from = waypoints[i];
      const to = waypoints[i + 1];

      const routeCoords = await fetchOsrmRoute(from.lat, from.lng, to.lat, to.lng);

      if (routeCoords) {
        // Determine segment type for coloring
        let segmentType = 'travel';
        if (from.type === 'driver') {
          segmentType = 'to_first_pickup';
        } else if (from.type === 'pickup') {
          segmentType = 'trip'; // Pickup to dropoff (active trip)
        } else if (from.type === 'dropoff') {
          segmentType = 'deadhead'; // Dropoff to next pickup (deadhead)
        }

        routeSegments.push({
          fromIndex: i,
          toIndex: i + 1,
          fromLabel: from.label,
          toLabel: to.label,
          segmentType,
          tripNumber: from.tripNumber || to.tripNumber,
          coordinates: routeCoords
        });
      }
    }

    setRoutes(routeSegments);
    setLoadingRoutes(false);
  }, []);

  // Trigger route fetching when driver changes (only in route view)
  useEffect(() => {
    console.log(`🔄 selectedDriver changed:`, selectedDriver, `viewMode:`, viewMode);
    if (selectedDriver && viewMode === 'route') {
      console.log(`   📍 Fetching routes for: ${selectedDriver.driver_name} (Route View)`);
      fetchRoutesForDriver(selectedDriver);
    } else {
      console.log(`   🗑️ Clearing routes (no driver selected or not in Route View)`);
      setRoutes([]);
      // Reset loading state when not in route view
      setLoadingRoutes(false);
    }
  }, [selectedDriver, viewMode, fetchRoutesForDriver]);

  // Get drivers with assigned trips
  const driversWithTrips = useMemo(() => {
    if (!displayDrivers.length) return [];
    return displayDrivers.filter(
      (d) => (d.trips && d.trips.length > 0) || (d.trip_count > 0)
    );
  }, [displayDrivers]);

  // Initialize visibleDriverZones with all drivers when in zone view
  React.useEffect(() => {
    if (viewMode === 'zone' && driversWithTrips.length > 0) {
      setVisibleDriverZones(driversWithTrips.map(d => d.driver_id));
      setSelectedTripId(null); // Clear trip selection when switching to zone view
    } else if (viewMode !== 'zone') {
      // Clear zone selections when not in zone view
      setVisibleDriverZones([]);
    }
  }, [viewMode, driversWithTrips]);

  // Generate driver zones for zone view
  const driverZones = useMemo(() => {
    if (!driversWithTrips || driversWithTrips.length === 0) return [];
    return generateDriverZones(driversWithTrips);
  }, [driversWithTrips]);

  // Build route points for selected driver
  const routeData = useMemo(() => {
    if (!selectedDriver || !selectedDriver.trips || selectedDriver.trips.length === 0) {
      return { markers: [], bounds: [] };
    }

    const markers = [];
    const bounds = [];

    // Driver current location
    // For busy drivers: real-time GPS position
    // For non-busy drivers: starting/home location
    const isBusyDriver = selectedDriver.is_busy === true;
    const driverLat = selectedDriver.current_latitude || selectedDriver.ref_latitude;
    const driverLng = selectedDriver.current_longitude || selectedDriver.ref_longitude;

    if (driverLat && driverLng) {
      markers.push({
        type: 'driver',
        position: [driverLat, driverLng],
        label: isBusyDriver ? 'Driver Current Position' : 'Driver Start',
        driverName: selectedDriver.driver_name,
        isBusy: isBusyDriver,
        profileImageURL: selectedDriver.profileImageURL || '',
      });
      bounds.push([driverLat, driverLng]);
    }

    // Sort trips by sequence number
    const sortedTrips = [...selectedDriver.trips].sort((a, b) => {
      const seqA = a.sequence ?? 999;
      const seqB = b.sequence ?? 999;
      return seqA - seqB;
    });

    // Create display numbers: busy trips (sequence 0) shown as "B1", "B2", planned trips as "1", "2"
    // Track busy trip count
    let busyTripCount = 0;

    // First pass: count busy trips
    sortedTrips.forEach(trip => {
      if (trip.is_busy_trip || trip.sequence === 0) {
        busyTripCount++;
      }
    });

    // Helper to check if two coordinates are the same (within ~50m tolerance)
    const isSameLocation = (lat1, lng1, lat2, lng2) => {
      if (!lat1 || !lng1 || !lat2 || !lng2) return false;
      const tolerance = 0.0005; // ~50 meters
      return Math.abs(lat1 - lat2) < tolerance && Math.abs(lng1 - lng2) < tolerance;
    };

    // Track previous dropoff to detect overlaps
    let prevDropoff = null;
    let currentBusyIndex = 0;
    let currentPlannedIndex = 0;

    sortedTrips.forEach((trip, index) => {
      const isBusyTrip = trip.is_busy_trip || trip.sequence === 0;

      // Determine display number
      let displayNumber;
      let tripLabel;
      if (isBusyTrip) {
        currentBusyIndex++;
        displayNumber = busyTripCount > 1 ? `B${currentBusyIndex}` : 'B';
        tripLabel = `Current Trip ${currentBusyIndex}`;
      } else {
        currentPlannedIndex++;
        displayNumber = currentPlannedIndex;
        tripLabel = `Trip ${currentPlannedIndex}`;
      }

      // Get ETA value (or available_in_minutes for busy trips)
      const etaValue = isBusyTrip ? trip.available_in_minutes : trip.eta_to_pickup_minutes;

      // Get pickup coordinates
      const pickupLat = trip.pickup_lat || trip.origin?.latitude;
      const pickupLng = trip.pickup_lng || trip.origin?.longitude;

      // Get dropoff coordinates
      const dropoffLat = trip.dropoff_lat || trip.destination?.latitude;
      const dropoffLng = trip.dropoff_lng || trip.destination?.longitude;

      // Check if this pickup overlaps with previous dropoff
      const overlapsWithPrevDropoff = prevDropoff &&
        isSameLocation(pickupLat, pickupLng, prevDropoff.lat, prevDropoff.lng);

      // Pickup marker (skip if overlapping - will be combined with previous dropoff)
      if (pickupLat && pickupLng && !overlapsWithPrevDropoff) {
        markers.push({
          type: 'pickup',
          position: [pickupLat, pickupLng],
          label: `${tripLabel} Pickup`,
          tripNumber: displayNumber,
          tripId: trip.trip_id,
          isBusyTrip,
          clientName: trip.client_name,
          address: trip.pickup_address,
          scheduledTime: trip.schedule_time,
          eta: etaValue,
          tripStatus: trip.trip_status,
        });
        bounds.push([pickupLat, pickupLng]);
      }

      // If there's overlap, update the previous dropoff marker to be a combined marker
      if (overlapsWithPrevDropoff && markers.length > 0) {
        const lastMarker = markers[markers.length - 1];
        if (lastMarker.type === 'dropoff') {
          lastMarker.type = 'combined';
          lastMarker.label = `${lastMarker.label} → ${tripLabel} Pickup`;
          lastMarker.nextPickupNumber = displayNumber;
          lastMarker.nextPickupTripId = trip.trip_id;
          lastMarker.nextClientName = trip.client_name;
          lastMarker.nextTripStatus = trip.trip_status;
        }
      }

      // Dropoff marker
      if (dropoffLat && dropoffLng) {
        markers.push({
          type: 'dropoff',
          position: [dropoffLat, dropoffLng],
          label: `${tripLabel} Dropoff`,
          tripNumber: displayNumber,
          tripId: trip.trip_id,
          isBusyTrip,
          clientName: trip.client_name,
          address: trip.dropoff_address,
          appointmentTime: trip.appointment_time,
          tripStatus: trip.trip_status,
        });
        bounds.push([dropoffLat, dropoffLng]);

        // Track for next iteration
        prevDropoff = { lat: dropoffLat, lng: dropoffLng };
      } else {
        prevDropoff = null;
      }
    });

    return { markers, bounds };
  }, [selectedDriver]);

  const formatTime = (timeStr) => {
    if (!timeStr) return 'N/A';
    try {
      const date = new Date(timeStr);
      return date.toLocaleString('en-US', {
        month: 'short',
        day: 'numeric',
        hour: 'numeric',
        minute: '2-digit',
        hour12: true,
        timeZone: companyTimezone
      });
    } catch {
      return timeStr;
    }
  };

  // Format break time - handles both pre-formatted strings (e.g. "10:00 AM") 
  // and ISO strings (e.g. "2026-01-28T09:00:00-08:00")
  const formatBreakTime = (timeStr) => {
    if (!timeStr) return 'N/A';
    
    // Convert to string in case it's not
    const str = String(timeStr);
    
    // If it's already a short time format (no 'T' = not ISO), return as-is
    if (str.indexOf('T') === -1) {
      return str;
    }
    
    // It's an ISO string - extract time without timezone conversion
    // Example: "2026-01-28T10:00:00-08:00" -> "10:00 AM"
    const tIndex = str.indexOf('T');
    if (tIndex !== -1 && str.length > tIndex + 5) {
      const timePart = str.substring(tIndex + 1, tIndex + 6); // "10:00"
      const [hourStr, minuteStr] = timePart.split(':');
      const hour24 = parseInt(hourStr, 10);
      const hour12 = hour24 % 12 || 12;
      const ampm = hour24 >= 12 ? 'PM' : 'AM';
      return `${hour12}:${minuteStr} ${ampm}`;
    }
    
    return str;
  };

  const formatShortDateTime = (timeStr) => {
    if (!timeStr) return 'N/A';
    try {
      const date = new Date(timeStr);
      return date.toLocaleString('en-US', {
        month: 'numeric',
        day: 'numeric',
        hour: 'numeric',
        minute: '2-digit',
        hour12: true,
        timeZone: companyTimezone
      });
    } catch {
      return typeof timeStr === 'string' ? timeStr : 'N/A';
    }
  };

  return (
    <div className="h-[calc(100vh-72px)] flex flex-col gap-3">
      {/* Map + Sidebar */}
      <div className="flex flex-1 min-h-0 gap-3">
        {/* Left Sidebar - Driver List */}
        <div className="w-72 bg-card-bg rounded-lg border border-card-border overflow-hidden flex flex-col">
          <div className="px-5 py-3.5 bg-main-primary text-white flex-shrink-0 shadow-lg flex flex-col items-start border-b border-white/10">
            <h2 className="font-black text-xl tracking-tight uppercase">Drivers</h2>
            <p className="text-[9px] font-bold text-white/50 uppercase tracking-[0.2em]">
              {driversWithTrips.length} drivers with trips
            </p>
          </div>

          <div className="flex-1 overflow-y-auto">
            {loading ? (
              <div className="flex items-center justify-center h-32">
                <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-main-primary"></div>
              </div>
            ) : !selectedResultId ? (
              <div className="p-4 text-center text-textMuted">
                <p className="text-sm">Select a planning session above to view driver routes</p>
              </div>
            ) : driversWithTrips.length === 0 ? (
              <div className="p-4 text-center text-textMuted">
                <p className="text-sm">No drivers with assigned trips</p>
              </div>
            ) : (
              <div className="divide-y divide-main-base">
                {driversWithTrips.map((driver) => {
                  const isSelected = selectedDriver?.driver_id === driver.driver_id;

                  return (
                    <div key={driver.driver_id}>
                      <button
                        onClick={() => {
                          setSelectedTripId(null);
                          setSelectedDriver(isSelected ? null : driver);
                        }}
                        className={`w-full text-left px-5 py-2.5 transition-all duration-200 ${isSelected
                            ? 'bg-main-base-100 shadow-inner ring-1 ring-main-primary/10'
                            : 'hover:bg-main-base-50/80'
                          }`}
                      >
                        <div className="flex items-center gap-1.5 flex-wrap">
                          <span className="font-black text-sm text-main-dark tracking-tight">{driver.driver_name}</span>
                          <DriverFreezeTypeBadge freezeType={driver.freeze_type} />
                          <DriverMultiloadBadge driver={driver} />
                        </div>
                        <div className="flex flex-col gap-1 mt-1.5">
                          {isFleetDriver(driver) ? (
                            <div className="flex flex-col gap-0.5">
                              {(driver.fleet_windows || []).map((window) => (
                                <div
                                  key={`${window.window_index}-${window.fleet_id || window.rig_no}`}
                                  className="flex items-center gap-1.5 text-[10px] font-bold text-main-light uppercase tracking-tight"
                                >
                                  {`Rig ${window.rig_no || window.fleet_id || 'Fleet'}`}
                                </div>
                              ))}
                            </div>
                          ) : (
                            <div className="flex items-center gap-1.5 text-[10px] font-bold text-main-light uppercase tracking-tight">
                              <Clock className="w-3 h-3 opacity-60" />
                              {(() => {
                                try {
                                  const startDate = new Date(driver.shift_window.shift_start);
                                  const endDate = new Date(driver.shift_window.shift_end);
                                  return `${startDate.toLocaleString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true, timeZone: companyTimezone })} - ${endDate.toLocaleString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true, timeZone: companyTimezone })}`;
                                } catch { return 'Invalid shift'; }
                              })()}
                            </div>
                          )}
                          <div className="flex items-center gap-4 text-[9px] font-black uppercase tracking-wider">
                            <span className="flex items-center gap-1.5 text-main-light">
                              <Truck className="w-3 h-3 opacity-50" />
                              <span className="text-main-primary">{(driver.trips?.length || driver.trip_count || 0)}</span> trips • <span className="text-main-primary">{driver.total_distance_miles?.toFixed(1) || 0}</span> mi
                            </span>
                            <span className="flex items-center gap-1 text-orange-600/80">
                              <Navigation className="w-2.5 h-2.5 opacity-60" />
                              DH: {(driver.trips?.length
                                ? sumDeadheadMilesForTotal(driver.trips)
                                : driver.total_deadhead_miles || 0
                              ).toFixed(1)} mi
                            </span>
                          </div>
                        </div>
                      </button>

                      {isSelected && loadingDriverTrips && (
                        <div className="bg-main-base-100 border-l-[3px] border-main-primary px-4 py-6 flex justify-center">
                          <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-main-primary" />
                        </div>
                      )}

                      {isSelected && !loadingDriverTrips && driver.trips && driver.trips.length > 0 && (
                        <div className="bg-main-base-100 border-l-[3px] border-main-primary px-4 py-4 space-y-4">
                          {(() => {
                            // Calculate sections fresh for this render to avoid stale data
                            const sortedTrips = sortTripsForDisplay(driver.trips || []);
                            const sections = buildTripSections(sortedTrips);
                            const multiloadRoutes = assignmentData?.multiload_routes || {};
                            
                            let busyCount = sortedTrips.filter(t => t.is_busy_trip || t.sequence === 0).length;
                            let currentBusy = 0;
                            let currentPlanned = 0;
                            const out = [];

                            sections.forEach((section) => {
                              const isMultiload = section.multiloadGroupId != null;
                              if (isMultiload) {
                                const routeSteps = multiloadRoutes[section.multiloadGroupId];
                                let routeStr = null;
                                if (routeSteps && Array.isArray(routeSteps) && routeSteps.length > 0) {
                                  const tripIdToLabel = {};
                                  let n = 1;
                                  routeSteps.forEach(({ trip_id }) => {
                                    if (trip_id && tripIdToLabel[trip_id] == null) tripIdToLabel[trip_id] = n++;
                                  });
                                  routeStr = routeSteps
                                    .map(({ trip_id, stop_type }) => {
                                      const label = tripIdToLabel[trip_id] || '?';
                                      return stop_type === 'pickup' ? `P${label}` : `D${label}`;
                                    })
                                    .join(' → ');
                                }
                                out.push(
                                  <div key={`ml-${section.multiloadGroupId}`} className="border border-main-primary/30 rounded-xl p-2 mb-2 bg-main-primary/5 space-y-2">
                                    {routeStr && (
                                      <div className="border border-main-primary/30 rounded-lg p-2 bg-main-primary/10 text-sm">
                                        <span className="text-main-primary font-bold inline-flex items-center gap-1">
                                          <Truck className="w-3.5 h-3.5" />
                                          Route:
                                        </span>{' '}
                                        <span className="font-mono font-bold text-main-primary">{routeStr}</span>
                                      </div>
                                    )}
                                    {section.trips.map((trip, idx) => renderTripCard(trip, idx))}
                                  </div>
                                );
                                return;
                              }

                              section.trips.forEach((trip, idx) => {
                                out.push(<React.Fragment key={trip.trip_id || `s-${idx}`}>{renderTripCard(trip, idx)}</React.Fragment>);
                              });
                            });

                            function renderTripCard(trip, index) {
                              const isBusy = trip.is_busy_trip || trip.sequence === 0;
                              const isBreak = trip.is_break === true;
                              let displayLabel;
                              let bgColor;

                              if (isBreak) {
                                currentPlanned++;
                                displayLabel = currentPlanned;
                                const breakDuration = trip.break_duration_minutes || 0;
                                const breakStartDisplay = formatBreakTime(trip.break_start || trip.schedule_time);
                                const breakEndDisplay = formatBreakTime(trip.break_end || trip.appointment_time);
                                return (
                                  <div key={trip.trip_id || index} className="bg-teal-50/60 border border-dashed border-teal-200 rounded-xl p-4 shadow-sm overflow-hidden">
                                     <div className="flex items-center gap-3 mb-3">
                                      <div className="w-8 h-8 rounded-full bg-teal-100 flex items-center justify-center text-teal-600">
                                        <RiRestTimeFill className="text-lg" />
                                      </div>
                                      <span className="font-bold text-sm text-slate-800">{trip.client_name || 'Rest Break'}</span>
                                      <span className="text-[10px] bg-teal-500 text-white px-2 py-0.5 rounded-full font-bold uppercase tracking-wider ml-auto">Break</span>
                                    </div>
                                    <div className="space-y-2 text-[11px]">
                                      <div className="flex items-center justify-between py-1 border-b border-teal-100/50">
                                        <span className="text-slate-500 font-medium">Duration:</span>
                                        <span className="font-bold text-slate-800">{Math.round(breakDuration)} min</span>
                                      </div>
                                      <div className="flex items-center justify-between py-1 border-b border-teal-100/50">
                                        <span className="text-slate-500 font-medium">Start Time:</span>
                                        <span className="font-mono font-bold text-teal-700">{breakStartDisplay}</span>
                                      </div>
                                      <div className="flex items-center justify-between py-1">
                                        <span className="text-slate-500 font-medium">End Time:</span>
                                        <span className="font-mono font-bold text-teal-700">{breakEndDisplay}</span>
                                      </div>
                                    </div>
                                  </div>
                                );
                              }

                              const mapTripStatus = (trip.trip_status || '').toLowerCase();
                              const mapBusyStyle = isBusy ? (() => {
                                switch (mapTripStatus) {
                                  case 'onway':   return { bg: 'bg-blue-50/50 border-blue-200', circle: 'bg-blue-500', label: 'Onway' };
                                  case 'arrived': return { bg: 'bg-orange-50/50 border-orange-200', circle: 'bg-orange-500', label: 'On Scene' };
                                  case 'started': return { bg: 'bg-emerald-50/50 border-emerald-200', circle: 'bg-emerald-500', label: 'Picked Up' };
                                  default:        return { bg: 'bg-sky-50/50 border-sky-200', circle: 'bg-sky-500', label: trip.trip_status };
                                }
                              })() : null;

                              if (isBusy) {
                                currentBusy++;
                                displayLabel = busyCount > 1 ? `B${currentBusy}` : 'B';
                                bgColor = mapBusyStyle.circle;
                              } else {
                                currentPlanned++;
                                displayLabel = currentPlanned;
                                bgColor = 'bg-emerald-500';
                              }

                              const isTripSelected = viewMode === 'route' && selectedTripId === trip.trip_id;
                              const isClickable = viewMode === 'route' && trip.trip_id;
                              
                              return (
                                <div
                                  key={trip.trip_id || index}
                                  onClick={isClickable ? () => setSelectedTripId(isTripSelected ? null : trip.trip_id) : undefined}
                                  className={`
                                    bg-white border rounded-xl shadow-sm transition-all duration-200 relative overflow-hidden flex flex-col
                                    ${isClickable ? 'cursor-pointer hover:shadow-lg' : ''}
                                    ${isTripSelected ? 'shadow-md ring-1 ring-main-primary/20 bg-main-base-50/30' : 'border-slate-100'}
                                  `}
                                >


                                  <div className="p-4 flex-1">
                                    <div className="flex items-start justify-between mb-4">
                                      <div className="space-y-0.5">
                                        <h4 className="font-black text-sm text-[#2B3674] tracking-tight leading-tight uppercase">
                                          {trip.client_name}
                                        </h4>
                                        <p className="text-[9px] font-bold text-main-light uppercase tracking-widest truncate max-w-[140px]">
                                          {trip.trip_id || 'REF-N/A'}
                                        </p>
                                      </div>
                                      <div className="flex items-center gap-1">
                                        {trip.manually_adjusted === true && (
                                          <span className="inline-flex items-center rounded-md bg-amber-500 px-1.5 py-0.5 text-[9px] font-black text-white uppercase tracking-tighter" title="Manually adjusted">
                                            Manual
                                          </span>
                                        )}
                                        {trip.new_assignment === true && (
                                          <span className="inline-flex items-center rounded-md bg-violet-600 px-1.5 py-0.5 text-[9px] font-black text-white uppercase tracking-tighter" title="Newly assigned in this run">
                                            New
                                          </span>
                                        )}
                                        <div className="flex items-center justify-center bg-orange-700/90 text-white min-w-[32px] px-1.5 py-0.5 rounded-md text-[9px] font-black uppercase tracking-tighter">
                                          P({displayLabel})
                                        </div>
                                      </div>
                                    </div>

                                    {/* Timeline Section */}
                                    <div className="flex flex-col mt-2">
                                      {/* Pickup */}
                                      <div className="relative pb-4">
                                        <span aria-hidden="true" className="absolute inset-y-0 mt-1 left-2 ml-px h-full w-0.5 border-l border-dashed border-main-light opacity-30" />
                                        <div className="relative flex items-start gap-x-2">
                                          <div className="h-5 w-5 bg-container-base rounded-full overflow-hidden grid place-content-center flex-shrink-0 z-10 border border-main-base shadow-sm">
                                            <svg className="w-4 h-4 text-main-success" fill="none" stroke="currentColor" strokeWidth="1.5" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                                              <path d="M15 10.5a3 3 0 11-6 0 3 3 0 016 0z" strokeLinecap="round" strokeLinejoin="round" />
                                              <path d="M19.5 10.5c0 7.142-7.5 11.25-7.5 11.25S4.5 17.642 4.5 10.5a7.5 7.5 0 1115 0z" strokeLinecap="round" strokeLinejoin="round" />
                                            </svg>
                                          </div>
                                          <div className="min-w-0 flex-1 group/addr relative">
                                            <div className="text-[11px] font-black text-main-secondary leading-tight line-clamp-2" title={trip.pickup_address}>
                                              {trip.pickup_address}
                                            </div>
                                            <div className="text-[10px] font-bold text-main-light flex items-center gap-1 mt-0.5">
                                              <Clock className="w-3 h-3 opacity-60" />
                                              {formatShortDateTime(trip.schedule_time)}
                                            </div>
                                          </div>
                                        </div>
                                      </div>

                                      {/* Dropoff */}
                                      <div className="relative">
                                        <div className="relative flex items-start gap-x-2">
                                          <div className="h-5 w-5 bg-container-base rounded-full overflow-hidden grid place-content-center flex-shrink-0 z-10 border border-main-base shadow-sm">
                                            <svg className="w-4 h-4 text-main-danger" fill="none" stroke="currentColor" strokeWidth="1.5" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                                              <path d="M15 10.5a3 3 0 11-6 0 3 3 0 016 0z" strokeLinecap="round" strokeLinejoin="round" />
                                              <path d="M19.5 10.5c0 7.142-7.5 11.25-7.5 11.25S4.5 17.642 4.5 10.5a7.5 7.5 0 1115 0z" strokeLinecap="round" strokeLinejoin="round" />
                                            </svg>
                                          </div>
                                          <div className="min-w-0 flex-1 group/addr relative">
                                            <div className="text-[11px] font-black text-main-secondary leading-tight line-clamp-2" title={trip.dropoff_address}>
                                              {trip.dropoff_address}
                                            </div>
                                            <div className="text-[10px] font-bold text-main-light flex items-center gap-1 mt-0.5">
                                              <Clock className="w-3 h-3 opacity-60" />
                                              {formatShortDateTime(trip.appointment_time)}
                                            </div>
                                          </div>
                                        </div>
                                      </div>
                                    </div>
                                  </div>

                                  {/* Footer Info Bar */}
                                  <div className="bg-slate-50/50 border-t border-slate-100 px-4 py-2.5 flex items-center justify-between">
                                    <div className="flex items-center gap-2 text-[10px] font-black text-main-light uppercase tracking-tight">
                                      <Car className="w-3.5 h-3.5 text-main-light" />
                                      {(() => {
                                        const los = trip.los_title || trip.losTitle || trip.vehicle_type || trip.los_type || trip.space_type;
                                        const space = trip.space_type_title || trip.spaceTypeTitle || '';
                                        return (
                                          <>
                                            {los}
                                            {space && <span className="text-main-light font-normal ml-1">({space})</span>}
                                          </>
                                        );
                                      })()}
                                    </div>
                                    <div className="flex items-center gap-1.5 text-[10px] font-black text-[#2B3674]">
                                      <Gauge className="w-3.5 h-3.5 text-main-light" />
                                      {trip.trip_distance_miles?.toFixed(2) || '0.00'} mi
                                    </div>
                                  </div>


                                </div>
                              );
                            }

                            return out;
                          })()}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>

        {/* Right Side - Map (Always rendered) */}
        <div className="flex-1 bg-card-bg rounded-lg border border-card-border overflow-hidden relative">
          {/* View Mode Toggle - Top Right */}
          <div className="absolute top-4 right-4 z-[1000] bg-white rounded-lg shadow-lg p-1 flex">
            <button
              onClick={() => setViewMode('route')}
              className={`px-3 py-2 text-sm font-medium rounded-md transition-colors ${
                viewMode === 'route'
                  ? 'btn-primary'
                  : 'btn-white'
              }`}
            >
              Route View
            </button>
            <button
              onClick={() => setViewMode('zone')}
              className={`px-3 py-2 text-sm font-medium rounded-md transition-colors ${
                viewMode === 'zone'
                  ? 'btn-primary'
                  : 'btn-white'
              }`}
            >
              Zone View
            </button>
          </div>
          {/* Overlay messages */}
          {!selectedResultId && (
            <div className="absolute inset-0 z-[500] bg-white/80 flex items-center justify-center pointer-events-none">
              <div className="text-center">
                <div className="flex justify-center mb-4">
                  <Map className="w-10 h-10 text-gray-400" />
                </div>
                <p className="text-lg font-medium text-textPrimary">Select a Planning Session</p>
                <p className="text-sm mt-1 text-textMuted">Choose a session from the dropdown above to view routes</p>
              </div>
            </div>
          )}

          {selectedResultId && viewMode === 'route' && !selectedDriver && (
            <div className="absolute inset-0 z-[500] bg-white/80 flex items-center justify-center pointer-events-none">
              <div className="text-center">
                <div className="flex justify-center mb-4">
                  <ArrowLeft className="w-10 h-10 text-gray-400" />
                </div>
                <p className="text-lg font-medium text-textPrimary">Select a Driver</p>
                <p className="text-sm mt-1 text-textMuted">Click on a driver from the list to view their route</p>
              </div>
            </div>
          )}

          {selectedResultId && viewMode === 'zone' && driversWithTrips.length === 0 && (
            <div className="absolute inset-0 z-[500] bg-white/80 flex items-center justify-center pointer-events-none">
              <div className="text-center">
                <div className="flex justify-center mb-4">
                  <Ban className="w-10 h-10 text-gray-400" />
                </div>
                <p className="text-lg font-medium text-textPrimary">No Drivers with Trips</p>
                <p className="text-sm mt-1 text-textMuted">No driver zones to display</p>
              </div>
            </div>
          )}

          {/* Driver Info Header - Only show when driver selected */}
          {selectedDriver && (
            <div className="absolute top-20 left-4 z-[1000] bg-white rounded-lg shadow-lg p-3 max-w-sm">
              <div className="flex items-center gap-1.5">
                <div className="font-semibold text-gray-900">{selectedDriver.driver_name}</div>
                <DriverFreezeTypeBadge freezeType={selectedDriver.freeze_type} />
                <DriverMultiloadBadge driver={selectedDriver} />
              </div>
              <div className="text-sm text-gray-600 mt-1">
                {(selectedDriver.trips?.length || selectedDriver.trip_count || 0)} trips • {selectedDriver.total_distance_miles?.toFixed(1) || 0} miles
              </div>
            </div>
          )}

          {/* Routes Loading Indicator */}
          {loadingRoutes && (
            <>
              {/* Blur overlay for map */}
              <div className="absolute inset-0 z-[900] bg-black/20 backdrop-blur-sm pointer-events-none"></div>

              {/* Centered loading indicator */}
              <div className="absolute inset-0 z-[1000] flex items-center justify-center pointer-events-none">
                <div className="bg-white rounded-lg shadow-lg p-4 flex items-center gap-3 pointer-events-auto">
                  <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-blue-500"></div>
                  <span className="text-sm text-gray-700 font-medium">Loading routes...</span>
                </div>
              </div>
            </>
          )}

          {/* Legend - Show different legends for different view modes */}
          {viewMode === 'route' && selectedDriver && (
            <div className="absolute bottom-4 left-4 z-[1000] bg-white rounded-lg shadow-lg p-3">
              <div className="text-xs font-semibold text-gray-700 mb-2">Markers</div>
              <div className="space-y-1 text-xs">
                <div className="flex items-center gap-2">
                  <div className="w-4 h-4 rounded-full bg-blue-500 flex items-center justify-center text-white text-[8px] font-bold">D</div>
                  <span>Driver Start</span>
                </div>
                <div className="flex items-center gap-2">
                  <div className="w-4 h-4 rounded-full bg-amber-500 flex items-center justify-center text-white">
                    <Car className="w-4 h-4" />
                  </div>
                  <span>Driver (busy/moving)</span>
                </div>
                <div className="flex items-center gap-2">
                  <div className="w-4 h-4 rounded-full bg-green-500"></div>
                  <span>Pickup</span>
                </div>
                <div className="flex items-center gap-2">
                  <div className="w-4 h-4 rounded-full bg-red-500"></div>
                  <span>Dropoff</span>
                </div>
                <div className="flex items-center gap-2">
                  <div className="w-4 h-4 rounded-full bg-amber-500 flex items-center justify-center text-white">
                    <Car className="w-4 h-4" />
                  </div>
                  <span>In Progress (B)</span>
                </div>
                <div className="flex items-center gap-2">
                  <div className="flex flex-col items-center">
                    <div className="w-3 h-3 rounded-full bg-red-500 -mb-1"></div>
                    <div className="w-3 h-3 rounded-full bg-green-500"></div>
                  </div>
                  <span>Combined (same location)</span>
                </div>
              </div>
              <div className="text-xs font-semibold text-gray-700 mt-3 mb-2">Route</div>
              <div className="space-y-1 text-xs">
                <div className="flex items-center gap-2">
                  <div className="w-6 h-1 bg-blue-500 rounded"></div>
                  <span>To First Pickup</span>
                </div>
                <div className="flex items-center gap-2">
                  <div className="w-6 h-1 bg-green-500 rounded"></div>
                  <span>Active Trip</span>
                </div>
                <div className="flex items-center gap-2">
                  <div className="w-6 h-1 bg-amber-500 rounded" style={{ backgroundImage: 'repeating-linear-gradient(90deg, #F59E0B 0px, #F59E0B 4px, transparent 4px, transparent 8px)' }}></div>
                  <span>Deadhead</span>
                </div>
              </div>
            </div>
          )}

          {/* Zone View Legend - Driver zone visibility controls */}
          {viewMode === 'zone' && driversWithTrips.length > 0 && (
            <div className="absolute bottom-4 left-4 z-[1000] bg-white rounded-lg shadow-lg p-3 max-w-sm">
              <div className="text-xs font-semibold text-gray-700 mb-2">Driver Zones & Markers</div>

              {/* Select All / Deselect All buttons */}
              <div className="flex gap-2 mb-3">
                <button
                  onClick={() => setVisibleDriverZones(driversWithTrips.map(d => d.driver_id))}
                  className="btn-primary text-xs px-2 py-1"
                >
                  Select All
                </button>
                <button
                  onClick={() => setVisibleDriverZones([])}
                  className="btn-white text-xs px-2 py-1"
                >
                  Deselect All
                </button>
              </div>

              {/* Driver checkboxes */}
              <div className="space-y-1 max-h-48 overflow-y-auto">
                {(() => {
                  const driverColors = generateDriverColors(driversWithTrips.length);
                  return driversWithTrips.map((driver, index) => {
                    const driverColor = driverColors[index];
                    const isZoneVisible = visibleDriverZones.includes(driver.driver_id);
                    const isRouteSelected = selectedDriver?.driver_id === driver.driver_id;

                    return (
                      <div key={driver.driver_id} className={`flex items-center gap-2 p-1.5 rounded ${isRouteSelected ? 'bg-main-primary/10 border border-main-primary/30' : ''}`}>
                        <input
                          type="checkbox"
                          checked={isZoneVisible}
                          onChange={(e) => {
                            if (e.target.checked) {
                              setVisibleDriverZones(prev => [...prev, driver.driver_id]);
                            } else {
                              setVisibleDriverZones(prev => prev.filter(id => id !== driver.driver_id));
                            }
                          }}
                          className="w-3 h-3"
                        />
                        <div
                          className="w-3 h-3 rounded flex-shrink-0"
                          style={{ backgroundColor: driverColor }}
                        ></div>
                        <span className="text-xs font-medium text-gray-700 truncate">{driver.driver_name}</span>
                        {isRouteSelected && <span className="text-xs text-blue-600 font-semibold">(Route)</span>}
                      </div>
                    );
                  });
                })()}
              </div>
              <div className="text-xs text-gray-500 mt-2 pt-2 border-t border-gray-200">
                Check to show zones • Markers are colored by driver • pulse when selected in sidebar
              </div>
            </div>
          )}

          {/* The Map - Always rendered, never unmounted */}
          <style>{`
            @keyframes markerPulse {
              0% {
                transform: scale(1);
                filter: drop-shadow(0 0 4px rgba(59, 130, 246, 0.4));
              }
              50% {
                transform: scale(1.2);
                filter: drop-shadow(0 0 8px rgba(59, 130, 246, 0.8));
              }
              100% {
                transform: scale(1);
                filter: drop-shadow(0 0 4px rgba(59, 130, 246, 0.4));
              }
            }
          `}</style>
          <div className="h-full w-full">
            <PlannerLeafletMap
              routeData={routeData}
              routes={routes}
              formatTime={formatTime}
              viewMode={viewMode}
              driverZones={driverZones}
              selectedDriver={selectedDriver}
              driversWithTrips={driversWithTrips}
              visibleDriverZones={visibleDriverZones}
              selectedTripId={selectedTripId}
            />
          </div>
        </div>
      </div>

      {/* Trip Details Table - clearly separated below map */}
      {selectedDriver && selectedDriver.trips && selectedDriver.trips.length > 0 && (
        <div className="flex-shrink-0 border border-main-base rounded-lg shadow-sm overflow-hidden bg-white">
          <div className="px-3 py-1.5 border-b border-main-base bg-white">
            <h3 className="font-semibold text-xs text-textPrimary">
              Trip Details — {selectedDriver.driver_name}
            </h3>
          </div>
          <div className="overflow-x-auto">
            <div className="max-h-[260px] overflow-y-auto">
            <table className="min-w-full divide-y divide-main-base">
              <thead>
                <tr className="divide-x divide-gray-200">
                  <th className="sticky top-0 z-10 border-b border-gray-300 bg-white p-[3px] text-center text-xs font-semibold text-gray-900 whitespace-nowrap">#</th>
                  <th className="sticky top-0 z-10 border-b border-gray-300 bg-white p-[3px] text-left text-xs font-semibold text-gray-900 whitespace-nowrap">Client</th>
                  <th className="sticky top-0 z-10 border-b border-gray-300 bg-white p-[3px] text-left text-xs font-semibold text-gray-900 whitespace-nowrap">Pickup Address</th>
                  <th className="sticky top-0 z-10 border-b border-gray-300 bg-white p-[3px] text-left text-xs font-semibold text-gray-900 whitespace-nowrap">Dropoff Address</th>
                  <th className="sticky top-0 z-10 border-b border-gray-300 bg-white p-[3px] text-left text-xs font-semibold text-gray-900 whitespace-nowrap">Distance</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-300 bg-white">
                {(() => {
                  const sortedTrips = sortTripsForDisplay(selectedDriver.trips || []);
                  const sections = buildTripSections(sortedTrips);
                  const multiloadRoutes = assignmentData?.multiload_routes || {};

                  let busyCount = sortedTrips.filter(t => t.is_busy_trip || t.sequence === 0).length;
                  let currentBusy = 0;
                  let currentPlanned = 0;
                  const rows = [];
                  let globalIndex = 0;

                  sections.forEach((section) => {
                    const isMultiloadSection = !!section.multiloadGroupId;
                    const sectionTripCount = section.trips.length;
                    section.trips.forEach((trip, idxInSection) => {
                      const index = globalIndex++;
                      const isFirstInSection = idxInSection === 0;
                      const isLastInSection = idxInSection === sectionTripCount - 1;
                      const multiloadFirstRow = isMultiloadSection && isFirstInSection; // used as key for route header row insertion
                      const multiloadLastRow = isMultiloadSection && isLastInSection;
                      const multiloadTripClasses = isMultiloadSection
                        ? `bg-main-primary/5 border-l-4 border-r border-main-primary ${multiloadLastRow ? 'border-b-2' : ''}`
                        : '';
                      const isBusy = trip.is_busy_trip || trip.sequence === 0;
                      const isBreak = trip.is_break === true;

                      // Multiload section: header band at top of group
                      if (isMultiloadSection && isFirstInSection) {
                        const routeSteps = multiloadRoutes[section.multiloadGroupId];
                        let routeStr = null;
                        if (routeSteps && Array.isArray(routeSteps) && routeSteps.length > 0) {
                          const tripIdToLabel = {};
                          let n = 1;
                          routeSteps.forEach(({ trip_id }) => {
                            if (trip_id && tripIdToLabel[trip_id] == null) tripIdToLabel[trip_id] = n++;
                          });
                          routeStr = routeSteps
                            .map(({ trip_id, stop_type }) => {
                              const label = tripIdToLabel[trip_id] || '?';
                              return stop_type === 'pickup' ? `P${label}` : `D${label}`;
                            })
                            .join(' → ');
                        }
                        rows.push(
                          <tr key={`ml-route-${section.multiloadGroupId}`} className="border-t-2 border-l-4 border-r border-main-primary bg-main-primary">
                            <td colSpan="5" className="px-2 py-1">
                              <div className="flex items-center gap-2">
                                <span className="inline-flex items-center gap-1 text-[10px] font-bold text-white">
                                  <Truck className="w-3 h-3 flex-shrink-0" />
                                  Multiload
                                </span>
                                {routeStr && (
                                  <>
                                    <span className="text-white/40 text-[10px]">·</span>
                                    <span className="text-[10px] font-mono font-bold text-white">{routeStr}</span>
                                  </>
                                )}
                                <span className="ml-auto text-[10px] text-white/60">{sectionTripCount} trips</span>
                              </div>
                            </td>
                          </tr>
                        );
                      }
                      let displayNum;
                      if (isBusy) {
                        currentBusy++;
                        displayNum = busyCount > 1 ? `B${currentBusy}` : 'B';
                      } else {
                        currentPlanned++;
                        displayNum = currentPlanned;
                      }

                      if (isBreak) {
                        const breakDuration = trip.break_duration_minutes || 0;
                        const breakStartDisplay = formatBreakTime(trip.break_start || trip.schedule_time);
                        const breakEndDisplay = formatBreakTime(trip.break_end || trip.appointment_time);
                        rows.push(
                          <tr key={trip.trip_id || index} className="divide-x divide-gray-200 bg-teal-50/60 border border-dashed border-teal-200">
                            <td className="p-[3px] text-xs text-main-dark whitespace-nowrap"><RiRestTimeFill className="text-teal-500 text-base" /></td>
                            <td className="p-[3px] text-xs text-main-dark whitespace-nowrap">
                              <div className="font-medium flex items-center gap-1">
                                {trip.client_name || 'Rest Break'}
                                <span className="text-[10px] bg-teal-500 text-white px-1 py-0.5 rounded-full">Break</span>
                              </div>
                            </td>
                            <td className="p-[3px] text-xs text-main-dark whitespace-nowrap">
                              <div className="text-main-light">Start</div>
                              <div className="font-mono">{breakStartDisplay}</div>
                            </td>
                            <td className="p-[3px] text-xs text-main-dark whitespace-nowrap">
                              <div className="text-main-light">End</div>
                              <div className="font-mono">{breakEndDisplay}</div>
                            </td>
                            <td className="p-[3px] text-xs text-main-dark whitespace-nowrap">
                              <span className="font-semibold">{Math.round(breakDuration)} min</span>
                              <div className="text-[10px] text-teal-600">#{trip.sequence}</div>
                            </td>
                          </tr>
                        );
                        return;
                      }

                      const tblStatus = (trip.trip_status || '').toLowerCase();
                      const tblBusyStyle = isBusy ? (() => {
                        switch (tblStatus) {
                          case 'onway':   return { row: 'bg-blue-50/50 hover:bg-blue-100/50', circle: 'bg-blue-500', badge: 'text-blue-700 bg-blue-100' };
                          case 'arrived': return { row: 'bg-orange-50/50 hover:bg-orange-100/50', circle: 'bg-orange-500', badge: 'text-orange-700 bg-orange-100' };
                          case 'started': return { row: 'bg-emerald-50/50 hover:bg-emerald-100/50', circle: 'bg-emerald-500', badge: 'text-emerald-700 bg-emerald-100' };
                          default:        return { row: 'bg-sky-50/50 hover:bg-sky-100/50', circle: 'bg-sky-500', badge: 'text-sky-700 bg-sky-100' };
                        }
                      })() : null;

                      const etaValue = isBusy ? trip.available_in_minutes : trip.eta_to_pickup_minutes;
                      const etaLabel = isBusy ? 'Avail' : 'ETA';
                      rows.push(
                        <tr key={trip.trip_id || index} className={`divide-x divide-gray-200 group text-main-dark ${isBusy ? tblBusyStyle.row : isMultiloadSection ? 'hover:bg-main-primary/10' : 'even:bg-main-base-100 hover:bg-main-base-50'} ${multiloadTripClasses}`}>
                          <td className="p-[3px] text-xs text-main-dark whitespace-nowrap">
                            <div className="flex flex-col items-center gap-0.5">
                              <span className={`inline-flex items-center justify-center w-5 h-5 rounded-full text-white text-[10px] font-bold ${isBusy ? tblBusyStyle.circle : isMultiloadSection ? 'bg-main-primary' : 'bg-main-primary'}`}>{displayNum}</span>
                              {isMultiloadSection && trip.multiload_pickup_order != null && (
                                <span className="text-[9px] font-bold text-main-primary leading-none">P{trip.multiload_pickup_order}</span>
                              )}
                            </div>
                          </td>
                          <td className="p-[3px] text-xs text-main-dark whitespace-nowrap">
                            <div className="font-medium">{trip.client_name}</div>
                            {isBusy && trip.trip_status && (
                              <span className={`text-[10px] ${tblBusyStyle.badge} px-1 py-0.5 rounded`}>
                                {(() => { const s = (trip.trip_status || '').toLowerCase(); if (s === 'arrived') return 'On Scene'; if (s === 'started') return 'Member Onboard'; if (s === 'onway') return 'Onway'; return trip.trip_status; })()}
                              </span>
                            )}
                          </td>
                          <td className="p-[3px] text-xs text-main-dark">
                            <div className="whitespace-nowrap">{trip.pickup_address || 'N/A'}</div>
                            <div className="text-main-light whitespace-nowrap">{formatTime(trip.schedule_time)}</div>
                          </td>
                          <td className="p-[3px] text-xs text-main-dark">
                            <div className="whitespace-nowrap">{trip.dropoff_address || 'N/A'}</div>
                            <div className="text-main-light whitespace-nowrap">{formatTime(trip.appointment_time)}</div>
                          </td>
                          <td className="p-[3px] text-xs text-main-dark whitespace-nowrap">
                            {trip.trip_distance_miles ? <div>{trip.trip_distance_miles.toFixed(1)} mi</div> : null}
                            {(trip.deadhead_miles > 0) && <div className="text-amber-600"><span className="text-main-light">DH:</span> {(trip.deadhead_miles).toFixed(1)} mi</div>}
                            {etaValue ? <div className="text-main-primary"><span className="text-main-light">{etaLabel}:</span> {Math.round(etaValue)} min</div> : null}
                          </td>
                        </tr>
                      );
                    });
                  });

                  return rows;
                })()}
              </tbody>
            </table>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default MapsPage;
